import React from 'react'
import { router } from '@inertiajs/react'
import type { ActionItemFilters as Filters, ActionItemCounts, User } from '../../types'
import { sortPeople } from '../../utils/peopleSort'

interface ActionItemFiltersProps {
  teamId: number
  filters: Filters
  counts: ActionItemCounts
  teamMembers: User[]
}

export default function ActionItemFilters({ teamId, filters, counts, teamMembers }: ActionItemFiltersProps) {
  function applyFilter(newFilters: Partial<Filters>) {
    const merged = { ...filters, ...newFilters }
    Object.keys(merged).forEach(k => {
      if (!merged[k as keyof Filters]) delete merged[k as keyof Filters]
    })
    router.get(`/teams/${teamId}/action_items`, merged as Record<string, string>, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  function clearFilters() {
    router.get(`/teams/${teamId}/action_items`, {}, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const activeStatus = filters.status || 'open'
  const hasActiveFilters = filters.owner_id || filters.search || filters.due_date_range || filters.sort

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1">
        <button
          onClick={() => applyFilter({ status: undefined })}
          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            activeStatus === 'open'
              ? 'bg-white shadow-sm text-text-primary border border-gray-200'
              : 'text-text-secondary hover:text-text-primary'
          }`}
        >
          Open
          <span className="ml-1.5 text-sm bg-[#F5F5F5] px-1.5 py-0.5 rounded-full">{counts.open}</span>
        </button>
        <button
          onClick={() => applyFilter({ status: 'completed' })}
          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            activeStatus === 'completed'
              ? 'bg-white shadow-sm text-text-primary border border-gray-200'
              : 'text-text-secondary hover:text-text-primary'
          }`}
        >
          Completed
          <span className="ml-1.5 text-sm bg-[#F5F5F5] px-1.5 py-0.5 rounded-full">{counts.completed}</span>
        </button>
        {counts.overdue > 0 && (
          <button
            onClick={() => applyFilter({ status: 'open', due_date_range: 'overdue' })}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              filters.due_date_range === 'overdue'
                ? 'bg-[rgba(237,28,36,0.15)] shadow-sm text-[#ED1C24] border border-[#ED1C24]/20'
                : 'text-[#ED1C24] hover:text-pinnacle-red-dark'
            }`}
          >
            Overdue
            <span className="ml-1.5 text-sm bg-[rgba(237,28,36,0.15)] text-[#ED1C24] px-1.5 py-0.5 rounded-full">{counts.overdue}</span>
          </button>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search action items..."
            defaultValue={filters.search || ''}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                applyFilter({ search: (e.target as HTMLInputElement).value || undefined })
              }
            }}
            onBlur={e => {
              if (e.target.value !== (filters.search || '')) {
                applyFilter({ search: e.target.value || undefined })
              }
            }}
            className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-md outline-none focus:border-charcoal"
          />
        </div>

        <select
          value={filters.owner_id || ''}
          onChange={e => applyFilter({ owner_id: e.target.value || undefined })}
          className="text-sm border border-gray-200 rounded-md px-3 py-1.5 outline-none focus:border-charcoal"
        >
          <option value="">All Owners</option>
          {sortPeople(teamMembers).map(m => (
            <option key={m.id} value={m.id}>{m.full_name}</option>
          ))}
        </select>

        {activeStatus === 'open' && (
          <select
            value={filters.due_date_range || ''}
            onChange={e => applyFilter({ due_date_range: e.target.value || undefined })}
            className="text-sm border border-gray-200 rounded-md px-3 py-1.5 outline-none focus:border-charcoal"
          >
            <option value="">All Dates</option>
            <option value="overdue">Overdue</option>
            <option value="today">Due Today</option>
            <option value="this_week">This Week</option>
          </select>
        )}

        <select
          value={filters.sort === 'manual' ? 'manual' : filters.sort ? `${filters.sort}_${filters.direction || 'asc'}` : ''}
          onChange={e => {
            if (e.target.value === 'manual') {
              applyFilter({ sort: 'manual', direction: undefined })
            } else if (e.target.value) {
              const idx = e.target.value.lastIndexOf('_')
              const sort = e.target.value.slice(0, idx)
              const direction = e.target.value.slice(idx + 1) as 'asc' | 'desc'
              applyFilter({ sort, direction })
            } else {
              applyFilter({ sort: undefined, direction: undefined })
            }
          }}
          className="text-sm border border-gray-200 rounded-md px-3 py-1.5 outline-none focus:border-charcoal"
        >
          <option value="">Default Sort</option>
          <option value="manual">Manual (drag to reorder)</option>
          <option value="owner_asc">Owner (A-Z)</option>
          <option value="owner_desc">Owner (Z-A)</option>
          <option value="due_date_asc">Due Date (Earliest)</option>
          <option value="due_date_desc">Due Date (Latest)</option>
          <option value="created_at_desc">Created (Newest)</option>
          <option value="created_at_asc">Created (Oldest)</option>
        </select>

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-sm text-text-secondary hover:text-text-primary underline"
          >
            Clear filters
          </button>
        )}
      </div>
    </div>
  )
}
